import {SortingState} from '@tanstack/table-core';
import React from 'react';
import Table from '@/components/table';
import useCreatorsTabColumns from '../../hooks/use-creators-tab-columns';
import type {
  CampaignCreatorRow,
  CreatorStatus,
  CampaignDeliverables,
  PageSelectionState,
} from '../../types/creatorTabTypes';

interface CreatorsTableProps {
  data: CampaignCreatorRow[];
  activeStatus: CreatorStatus;
  hasFulfillment?: boolean;
  deliverablesType?: CampaignDeliverables | null;
  sorting: SortingState;
  onSorting: (sorting: SortingState) => void;
  onActionComplete: () => void;
  onRowClick?: (row: CampaignCreatorRow) => void;
  visibleColumns?: Record<string, boolean>;
  selectedCreatorIds?: Set<string>;
  pageSelectionState?: PageSelectionState;
  onSelectionChange?: (creatorId: string, isSelected: boolean) => void;
  onSelectAllPage?: (isSelected: boolean) => void;
  rowSelection?: Record<string, boolean>;
  onEndReached?: () => void;
  isLoading?: boolean;
  /** IDs of creators currently shown in the preview side panel. */
  highlightedRowIds?: Set<string>;
  /**
   * Optional custom empty state shown when `data` is empty. When omitted, the
   * underlying Material React Table renders its default "No records to
   * display" message.
   */
  emptyStateComponent?: React.ReactNode;
}

const EMPTY_VISIBLE_COLUMNS: Record<string, boolean> = {};
const EMPTY_ROW_SELECTION: Record<string, boolean> = {};

function CreatorsTable({
  data,
  activeStatus,
  hasFulfillment = false,
  deliverablesType = null,
  sorting,
  onSorting,
  onActionComplete,
  onRowClick,
  visibleColumns = EMPTY_VISIBLE_COLUMNS,
  selectedCreatorIds,
  pageSelectionState,
  onSelectionChange,
  onSelectAllPage,
  rowSelection,
  onEndReached,
  isLoading,
  highlightedRowIds,
  emptyStateComponent,
}: CreatorsTableProps) {
  const columns = useCreatorsTabColumns({
    activeStatus,
    onActionComplete,
    hasFulfillment,
    deliverablesType,
    selectedCreatorIds,
    pageSelectionState,
    onSelectionChange,
    onSelectAllPage,
  });

  return (
    <Table
      data={data}
      columns={columns}
      sorting={sorting}
      onSorting={onSorting}
      isLoadingInitial={isLoading && data.length === 0}
      showProgressBars={false}
      visibleColumns={visibleColumns}
      rowSelection={rowSelection ?? EMPTY_ROW_SELECTION}
      enableSorting
      enableVirtualization
      manualSorting={false}
      onRowClick={onRowClick}
      onEndReached={onEndReached}
      ignorePortalRowClicks
      tabIndex={0}
      highlightedRowIds={highlightedRowIds}
      emptyStateComponent={emptyStateComponent}
    />
  );
}

export default CreatorsTable;
